import { useQueries, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  Building2Icon,
  InfoIcon,
  PackageIcon,
  SaveIcon,
  TruckIcon,
  WarehouseIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { StockTransferDetail } from "@/modules/inventory/presentation/api/client";
import {
  inventoryLotsApi,
  stockTransfersApi,
} from "@/modules/inventory/presentation/api/client";
import {
  useCancelStockTransfer,
  useReviseApprovedStockTransfer,
  useShipStockTransfer,
  useStockTransferQuery,
} from "@/modules/inventory/presentation/api/queries";
import { TransferCancelDialog } from "@/modules/inventory/presentation/ui/transfers/TransferCancelDialog";
import { TransferReviseConfirmDialog } from "@/modules/inventory/presentation/ui/transfers/TransferReviseConfirmDialog";
import { TransferShipConfirmDialog } from "@/modules/inventory/presentation/ui/transfers/TransferShipConfirmDialog";
import { TransferStatusBadge } from "@/modules/inventory/presentation/ui/transfers/TransferStatusBadge";
import {
  TRANSFER_CARD,
  TRANSFER_CARD_BODY_ONLY,
  TRANSFER_CARD_CONTENT,
  TRANSFER_CARD_CONTENT_TABLE,
  TRANSFER_CARD_HEADER,
  TRANSFER_CARD_HEADER_TABLE,
} from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import {
  TRANSFER_MENU_TITLE,
  TRANSFER_QTY_APPROVED_RULE,
  transferItemTypeLabel,
} from "@/modules/inventory/presentation/ui/transfers/transferLaoLabels";
import {
  hubQtyInsufficient,
  normalizeTransferQty,
  qtyApprovedMapFromTransfer,
  transferQtyEqual,
} from "@/modules/inventory/presentation/ui/transfers/transferQtyUtils";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";

export function TransferShipPage() {
  const { id } = useParams({ from: "/app/inventory/transfers/$id/ship" });
  const nav = useNavigate();
  const query = useStockTransferQuery(id);
  const transfer = query.data;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: TRANSFER_MENU_TITLE, to: "/app/inventory/transfers" },
            {
              label: transfer?.requestNo ?? "…",
              to: "/app/inventory/transfers/$id",
              params: { id },
            },
            { label: "ຈັດສົ່ງ" },
          ]}
        />
      </Header>
      <Main>
        <QueryState result={query} title="ກຳລັງໂຫຼດ" variant="fullscreen">
          {!transfer ? null : transfer.status !== "APPROVED" ? (
            <Card className={TRANSFER_CARD}>
              <CardContent
                className={`${TRANSFER_CARD_CONTENT} py-8 text-center`}
              >
                <p className="text-muted-foreground">
                  ສະຖານະບໍ່ແມ່ນອະນຸມັດແລ້ວ — ບໍ່ສາມາດຈັດສົ່ງໄດ້
                </p>
                <Button
                  className="mt-4"
                  variant="outline"
                  onClick={() =>
                    nav({
                      to: "/app/inventory/transfers/$id",
                      params: { id },
                    })
                  }
                >
                  <ArrowLeftIcon className="size-4" />
                  ກັບຄືນລາຍລະອຽດ
                </Button>
              </CardContent>
            </Card>
          ) : (
            <TransferShipApprovedBody transfer={transfer} id={id} />
          )}
        </QueryState>
      </Main>
    </>
  );
}

function TransferShipApprovedBody({
  transfer,
  id,
}: {
  transfer: StockTransferDetail;
  id: string;
}) {
  const nav = useNavigate();
  const ship = useShipStockTransfer(id);
  const revise = useReviseApprovedStockTransfer(id);
  const cancelMut = useCancelStockTransfer(id);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [qtyApproved, setQtyApproved] = useState<Record<string, number>>({});

  const hubBranchId = transfer.toBranchId;

  useEffect(() => {
    setQtyApproved(qtyApprovedMapFromTransfer(transfer));
  }, [transfer.id, transfer.updatedAt]);

  const availabilityQueries = useQueries({
    queries: transfer.lines.map((line) => ({
      queryKey: ["hub-avail", line.itemType, line.itemId] as const,
      queryFn: () =>
        stockTransfersApi.hubAvailability({
          itemType: line.itemType,
          itemId: line.itemId,
        }),
    })),
  });

  const lineQty = (line: (typeof transfer.lines)[number]) =>
    normalizeTransferQty(
      qtyApproved[line.id],
      normalizeTransferQty(line.qtyApproved, line.qtyRequested),
    );

  const buildReviseLines = () =>
    transfer.lines.map((l) => ({
      lineId: l.id,
      qtyApproved: lineQty(l),
    }));

  const hasShortage = useMemo(() => {
    return transfer.lines.some((line, i) => {
      const qty = lineQty(line);
      const avail = availabilityQueries[i]?.data?.qtyAvailable;
      const factor = line.toBaseFactor > 0 ? line.toBaseFactor : 1;
      const availableInUnit =
        typeof avail === "number" ? avail / factor : undefined;
      return hubQtyInsufficient(qty, availableInUnit);
    });
  }, [transfer.lines, qtyApproved, availabilityQueries]);

  const qtyDirty = useMemo(() => {
    return transfer.lines.some((line) => {
      const current = lineQty(line);
      const saved = normalizeTransferQty(line.qtyApproved, line.qtyRequested);
      return !transferQtyEqual(current, saved);
    });
  }, [transfer.lines, qtyApproved]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="flex items-center gap-2 font-bold font-mono text-2xl tracking-tight">
              <TruckIcon className="size-6 text-primary" />
              ຈັດສົ່ງ {transfer.requestNo}
            </h1>
            <TransferStatusBadge status={transfer.status} />
          </div>
          <p className="text-muted-foreground text-sm">
            ຕັດສະຕ໋ອກທີ່ Hub · ເລືອກ lot ແບບ FEFO · {transfer.lines.length} ບັນທັດ
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() =>
            nav({
              to: "/app/inventory/transfers/$id",
              params: { id },
            })
          }
        >
          <ArrowLeftIcon className="size-4" />
          ກັບຄືນ
        </Button>
      </div>

      {hasShortage && (
        <Card
          className={`${TRANSFER_CARD} border-destructive/30 bg-destructive/5`}
        >
          <CardContent
            className={`${TRANSFER_CARD_BODY_ONLY} flex gap-3 text-sm`}
          >
            <AlertTriangleIcon className="size-4 shrink-0 text-destructive" />
            <div>
              <p className="font-medium text-destructive">Hub ຄົງເຫຼືອບໍ່ພໍ</p>
              <p className="mt-1 text-muted-foreground">
                ແກ້ຈຳນວນອະນຸມັດໃນຕາຕະລາງແລ້ວກົດປຸ່ມບັນທຶກຈຳນວນ ຫຼື ຍົກເລີກຄຳຂໍ ແລ້ວໃຫ້ສາຂາຂໍໃໝ່
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className={`${TRANSFER_CARD} border-blue-500/20 bg-blue-500/5`}>
        <CardContent
          className={`${TRANSFER_CARD_BODY_ONLY} flex gap-3 text-sm`}
        >
          <InfoIcon className="mt-0.5 size-4 shrink-0 text-blue-600" />
          <p>
            ແກ້ຈຳນວນອະນຸມັດໃນຕາຕະລາງໄດ້ກ່ອນສົ່ງ ({TRANSFER_QTY_APPROVED_RULE}).
            ລະບົບເລືອກ lot ຕາມ FEFO ອັດຕະໂນມັດ.
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className={TRANSFER_CARD}>
          <CardHeader className={TRANSFER_CARD_HEADER}>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2Icon className="size-4" />
              ສາຂາປາຍທາງ
            </CardTitle>
          </CardHeader>
          <CardContent className={TRANSFER_CARD_CONTENT}>
            <p className="font-medium">
              {transfer.fromBranchCode} — {transfer.fromBranchName}
            </p>
          </CardContent>
        </Card>
        <Card className={TRANSFER_CARD}>
          <CardHeader className={TRANSFER_CARD_HEADER}>
            <CardTitle className="flex items-center gap-2 text-base">
              <WarehouseIcon className="size-4" />
              Hub (ຕັດສະຕ໋ອກ)
            </CardTitle>
          </CardHeader>
          <CardContent className={TRANSFER_CARD_CONTENT}>
            <p className="font-medium">
              {transfer.toBranchCode} — {transfer.toBranchName}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className={TRANSFER_CARD}>
        <CardHeader className={TRANSFER_CARD_HEADER_TABLE}>
          <CardTitle className="flex items-center gap-2 text-base">
            <PackageIcon className="size-4" />
            ລາຍການຈັດສົ່ງ
          </CardTitle>
        </CardHeader>
        <CardContent className={TRANSFER_CARD_CONTENT_TABLE}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ລາຍການ</TableHead>
                <TableHead className="text-right">ອະນຸມັດ (ແກ້ໄດ້)</TableHead>
                <TableHead className="text-right">Hub ຄົງເຫຼືອ</TableHead>
                <TableHead>Lot ແນະນຳ (ໝົດອາຍຸກ່ອນ)</TableHead>
                <TableHead className="w-[100px]">ສຖານະ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transfer.lines.map((line, index) => (
                <ShipLineRow
                  key={line.id}
                  hubBranchId={hubBranchId}
                  line={line}
                  qty={normalizeTransferQty(
                    qtyApproved[line.id],
                    normalizeTransferQty(line.qtyApproved, line.qtyRequested),
                  )}
                  onQtyChange={(v) =>
                    setQtyApproved((prev) => ({ ...prev, [line.id]: v }))
                  }
                  availability={(() => {
                    const avail =
                      availabilityQueries[index]?.data?.qtyAvailable;
                    if (typeof avail !== "number") return undefined;
                    const factor =
                      line.toBaseFactor > 0 ? line.toBaseFactor : 1;
                    return avail / factor;
                  })()}
                  availabilityLoading={availabilityQueries[index]?.isLoading}
                />
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className={`${TRANSFER_CARD} border-primary/20`}>
        <CardContent
          className={`${TRANSFER_CARD_BODY_ONLY} flex flex-wrap items-center justify-between gap-3`}
        >
          <p className="text-muted-foreground text-sm">
            {qtyDirty
              ? "ມີການແກ້ຈຳນວນ — ບັນທຶກກ່ອນສົ່ງ"
              : hasShortage
                ? "Hub ຄົງເຫຼືອຍັງບໍ່ພໍ — ຫຼຸດຈຳນວນອະນຸມັດແລ້ວບັນທຶກ"
                : "ພ້ອມຢືນຢັນສົ່ງ"}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={!qtyDirty}
              isLoading={revise.isPending}
              onClick={() => setReviseOpen(true)}
            >
              <SaveIcon className="size-4" />
              ບັນທຶກຈຳນວນ
            </Button>
            <Button variant="destructive" onClick={() => setCancelOpen(true)}>
              ຍົກເລີກຄຳຂໍ
            </Button>
            <Button
              disabled={hasShortage || qtyDirty}
              onClick={() => setConfirmOpen(true)}
            >
              <TruckIcon className="size-4" />
              ຢືນຢັນສົ່ງ
            </Button>
          </div>
        </CardContent>
      </Card>

      <TransferReviseConfirmDialog
        open={reviseOpen}
        requestNo={transfer.requestNo}
        loading={revise.isPending}
        onClose={() => setReviseOpen(false)}
        onConfirm={async () => {
          try {
            const item = await revise.mutateAsync({
              lines: buildReviseLines(),
            });
            setQtyApproved(qtyApprovedMapFromTransfer(item));
            toast.success("ບັນທຶກຈຳນວນອະນຸມັດແລ້ວ");
            setReviseOpen(false);
          } catch {
            toast.error(`ບັນທຶກບໍ່ສຳເລັດ — ${TRANSFER_QTY_APPROVED_RULE}`);
          }
        }}
      />

      <TransferShipConfirmDialog
        open={confirmOpen}
        requestNo={transfer.requestNo}
        loading={ship.isPending}
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          try {
            await ship.mutateAsync({});
            toast.success("ບັນທຶກຈັດສົ່ງແລ້ວ");
            setConfirmOpen(false);
            nav({
              to: "/app/inventory/transfers/$id",
              params: { id },
            });
          } catch {
            toast.error("ຈັດສົ່ງບໍ່ສຳເລັດ — ກວດສະຕ໋ອກ Hub");
          }
        }}
      />

      <TransferCancelDialog
        open={cancelOpen}
        requestNo={transfer.requestNo}
        loading={cancelMut.isPending}
        onClose={() => setCancelOpen(false)}
        onConfirm={async () => {
          try {
            await cancelMut.mutateAsync();
            toast.success("ຍົກເລີກຄຳຂໍແລ້ວ");
            setCancelOpen(false);
            nav({ to: "/app/inventory/transfers" });
          } catch {
            toast.error("ຍົກເລີກບໍ່ສຳເລັດ");
          }
        }}
      />
    </div>
  );
}

function ShipLineRow({
  hubBranchId,
  line,
  qty,
  onQtyChange,
  availability,
  availabilityLoading,
}: {
  hubBranchId: string;
  line: {
    id: string;
    itemName: string;
    itemType: "INGREDIENT" | "RETAIL_VARIANT";
    itemId: string;
    qtyRequested: number;
    unit: string;
    toBaseFactor: number;
  };
  qty: number;
  onQtyChange: (qty: number) => void;
  availability?: number;
  availabilityLoading?: boolean;
}) {
  const lots = useQuery({
    queryKey: ["hub-lots", hubBranchId, line.itemType, line.itemId],
    queryFn: () =>
      inventoryLotsApi.list({
        branchId: hubBranchId,
        itemType: line.itemType,
        itemId: line.itemId,
        sort: "fefo",
        limit: 5,
        offset: 0,
      }),
  });

  const factor = line.toBaseFactor > 0 ? line.toBaseFactor : 1;
  const lotHint =
    lots.data?.items
      .map((l) => {
        const qty = l.qtyRemaining / factor;
        return `${l.lotNo} (${qty.toLocaleString("lo-LA", {
          maximumFractionDigits: 2,
        })} ${line.unit})`;
      })
      .join(", ") || "—";

  const insufficient = hubQtyInsufficient(qty, availability);

  return (
    <TableRow>
      <TableCell>
        <span className="font-medium">{line.itemName}</span>
        <span className="ml-1 text-muted-foreground text-xs">
          ({transferItemTypeLabel(line.itemType)})
        </span>
        <p className="text-muted-foreground text-xs">
          ຂໍ {line.qtyRequested} {line.unit}
        </p>
      </TableCell>
      <TableCell className="text-right">
        <Input
          type="number"
          className="ml-auto w-28 text-right tabular-nums"
          min={0}
          max={line.qtyRequested}
          step="any"
          value={qty}
          onChange={(e) => onQtyChange(Number(e.target.value))}
        />
      </TableCell>
      <TableCell
        className={`text-right tabular-nums ${insufficient ? "font-medium text-destructive" : ""}`}
      >
        {availabilityLoading
          ? "…"
          : availability == null
            ? "—"
            : `${availability.toLocaleString("lo-LA", {
                maximumFractionDigits: 2,
              })} ${line.unit}`}
      </TableCell>
      <TableCell className="max-w-[240px] truncate text-muted-foreground text-sm">
        {lotHint}
      </TableCell>
      <TableCell>
        {availabilityLoading ? (
          <span className="text-muted-foreground text-xs">…</span>
        ) : insufficient ? (
          <Badge variant="destructive" className="text-xs">
            ບໍ່ພໍ
          </Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">
            ພ້ອມ
          </Badge>
        )}
      </TableCell>
    </TableRow>
  );
}
